import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, BellOff, Clock, Calendar, Save, Trash2, CalendarPlus, Download } from "lucide-react";
import { GlassCard } from "./GlassCard";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export const ReminderSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    times: ["09:00", "15:00", "21:00"],
    days: [0, 1, 2, 3, 4, 5, 6],
    message: "¿Cómo te sientes hoy?"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    fetchSettings();
    checkNotificationPermission();
  }, []);

  const checkNotificationPermission = () => {
    if (!("Notification" in window)) {
      setPermissionDenied(true);
      return;
    }
    setHasPermission(Notification.permission === "granted");
    setPermissionDenied(Notification.permission === "denied");
  };

  const requestPermission = async () => {
    if (!("Notification" in window)) {
      toast.error("Tu navegador no soporta notificaciones");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setHasPermission(permission === "granted");
      setPermissionDenied(permission === "denied");
      
      if (permission === "granted") {
        toast.success("¡Notificaciones activadas!");
        // Register push subscription
        await registerPushSubscription();
      } else if (permission === "denied") {
        toast.error("Permiso de notificaciones denegado");
      }
    } catch (error) {
      console.error("Permission error:", error);
      toast.error("Error al solicitar permisos");
    }
  };

  const registerPushSubscription = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("Push notifications not supported");
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          // This would be your VAPID public key - using a placeholder
          'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
        )
      });

      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      });
    } catch (error) {
      console.error("Push subscription error:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/user/reminders`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/user/reminders`, settings);
      toast.success("Recordatorios guardados");
      
      // Schedule local notifications if enabled
      if (settings.enabled && hasPermission) {
        scheduleLocalReminders();
      }
    } catch (error) {
      console.error("Error saving reminders:", error);
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const scheduleLocalReminders = () => {
    // For now, we'll use a simple approach with the browser's notification API
    // In production, this would be handled by a push notification service
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      console.log("Push notifications would be scheduled here");
    }
  };

  const toggleDay = (day) => {
    const newDays = settings.days.includes(day)
      ? settings.days.filter(d => d !== day)
      : [...settings.days, day].sort();
    setSettings({ ...settings, days: newDays });
  };

  const addTime = () => {
    if (settings.times.length >= 5) {
      toast.error("Máximo 5 recordatorios por día");
      return;
    }
    setSettings({
      ...settings,
      times: [...settings.times, "12:00"].sort()
    });
  };

  const removeTime = (index) => {
    const newTimes = settings.times.filter((_, i) => i !== index);
    setSettings({ ...settings, times: newTimes });
  };

  const updateTime = (index, value) => {
    const newTimes = [...settings.times];
    newTimes[index] = value;
    setSettings({ ...settings, times: newTimes.sort() });
  };

  if (loading) {
    return (
      <GlassCard className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3" />
          <div className="h-20 bg-white/10 rounded" />
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-orbitron text-lg text-white flex items-center gap-2">
          {settings.enabled ? (
            <Bell className="w-5 h-5 text-neon-cyan" />
          ) : (
            <BellOff className="w-5 h-5 text-slate-500" />
          )}
          Recordatorios
        </h3>
        <Switch
          checked={settings.enabled}
          onCheckedChange={(enabled) => {
            if (enabled && !hasPermission) {
              requestPermission();
            }
            setSettings({ ...settings, enabled });
          }}
          data-testid="reminder-toggle"
        />
      </div>

      {!hasPermission && !permissionDenied && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-4 p-3 rounded-lg bg-neon-yellow/10 border border-neon-yellow/30 text-sm"
        >
          <p className="text-neon-yellow">
            Activa las notificaciones para recibir recordatorios
          </p>
          <Button
            onClick={requestPermission}
            size="sm"
            className="mt-2 cyber-button-cyan"
          >
            <Bell className="w-4 h-4 mr-1" />
            Activar Notificaciones
          </Button>
        </motion.div>
      )}

      {permissionDenied && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
          Las notificaciones están bloqueadas. Actívalas en la configuración de tu navegador.
        </div>
      )}

      <div className={`space-y-6 ${!settings.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Days Selection */}
        <div>
          <label className="text-sm text-slate-400 mb-2 block flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Días de la semana
          </label>
          <div className="flex gap-2 flex-wrap">
            {DAYS_ES.map((day, index) => (
              <button
                key={index}
                onClick={() => toggleDay(index)}
                className={`px-3 py-1.5 rounded-lg text-sm font-rajdhani transition-all ${
                  settings.days.includes(index)
                    ? "bg-neon-cyan/20 border border-neon-cyan/50 text-neon-cyan"
                    : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>

        {/* Times */}
        <div>
          <label className="text-sm text-slate-400 mb-2 block flex items-center gap-1">
            <Clock className="w-3 h-3" /> Horas de recordatorio
          </label>
          <div className="space-y-2">
            {settings.times.map((time, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2"
              >
                <input
                  type="time"
                  value={time}
                  onChange={(e) => updateTime(index, e.target.value)}
                  className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:border-neon-cyan/50 focus:outline-none"
                />
                {settings.times.length > 1 && (
                  <button
                    onClick={() => removeTime(index)}
                    className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            ))}
            {settings.times.length < 5 && (
              <button
                onClick={addTime}
                className="text-sm text-neon-cyan hover:underline"
              >
                + Añadir hora
              </button>
            )}
          </div>
        </div>

        {/* Custom Message */}
        <div>
          <label className="text-sm text-slate-400 mb-2 block">
            Mensaje personalizado
          </label>
          <input
            type="text"
            value={settings.message}
            onChange={(e) => setSettings({ ...settings, message: e.target.value })}
            placeholder="¿Cómo te sientes hoy?"
            maxLength={100}
            className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white placeholder:text-slate-600 focus:border-neon-cyan/50 focus:outline-none"
          />
        </div>

        {/* Save Button */}
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="w-full cyber-button-cyan"
          data-testid="save-reminders-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando..." : "Guardar Recordatorios"}
        </Button>

        {/* Add to Calendar Button */}
        <div className="pt-4 border-t border-white/10">
          <p className="text-xs text-slate-500 mb-3">
            Sincroniza los recordatorios con tu calendario (Google, Apple, Outlook)
          </p>
          <Button
            onClick={() => {
              // Download .ics file
              const link = document.createElement('a');
              link.href = `${API}/user/reminders/calendar.ics`;
              link.download = 'moodpunk-recordatorios.ics';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              toast.success("Archivo descargado. Ábrelo para añadir a tu calendario.");
            }}
            variant="outline"
            className="w-full bg-white/5 border-white/20 hover:bg-white/10 text-slate-300"
            data-testid="add-to-calendar-btn"
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            Añadir al Calendario
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

// Helper function for VAPID key conversion
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
